import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  MessageSquare, Phone, Send, Save, Loader2, 
  CheckCircle, XCircle, TestTube, Eye, EyeOff, Info,
  Mail, Server, Lock, User, Calendar, LogIn, LogOut,
  Hotel, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

export default function CommunicationSettings({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState({ sms_providers: [], whatsapp_providers: [] });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testChannel, setTestChannel] = useState('sms');
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test message from BIZIONIX HMS.');
  const [testing, setTesting] = useState(false);
  
  // SMS Settings state
  const [smsSettings, setSmsSettings] = useState({
    provider: 'custom',
    api_key: '',
    api_secret: '',
    sender_id: '',
    api_url: '',
    sender_number: '',
    template_id: '',
    category: '',
    is_enabled: false
  });
  
  // WhatsApp Settings state
  const [whatsappSettings, setWhatsappSettings] = useState({
    provider: 'twilio',
    api_key: '',
    api_secret: '',
    phone_number_id: '',
    business_account_id: '',
    api_url: '',
    is_enabled: false
  });

  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'BIZIONIX HMS',
    smtp_use_tls: true
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  
  // Settings hierarchy
  const [useOwnCommunication, setUseOwnCommunication] = useState(false);
  const [settingsSource, setSettingsSource] = useState('default');

  // Notification Event Settings
  const [notificationSettings, setNotificationSettings] = useState({
    booking_enabled: true,
    checkin_enabled: true,
    checkout_enabled: true,
    booking_channels: ['email'],
    checkin_channels: ['email'],
    checkout_channels: ['email']
  });

  const [showSecrets, setShowSecrets] = useState({
    sms_api_key: false,
    sms_api_secret: false,
    wa_api_key: false,
    wa_api_secret: false,
    smtp_password: false
  });

  useEffect(() => {
    loadSettings();
    loadProviders();
    loadSmtpSettings();
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const res = await api.get('/communication/notification-settings');
      if (res.data) {
        setNotificationSettings(res.data);
      }
    } catch (error) {
      console.error('Failed to load notification settings');
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get('/communication/settings');
      if (res.data.sms) {
        setSmsSettings(res.data.sms);
      }
      if (res.data.whatsapp) {
        setWhatsappSettings(res.data.whatsapp);
      }
      // Load settings hierarchy info
      if (res.data.use_own_communication !== undefined) {
        setUseOwnCommunication(res.data.use_own_communication);
      }
      if (res.data.settings_source) {
        setSettingsSource(res.data.settings_source);
      }
    } catch (error) {
      toast.error('Failed to load communication settings');
    } finally {
      setLoading(false);
    }
  };

  const loadSmtpSettings = async () => {
    try {
      const response = await api.get('/settings/smtp');
      setSmtpConfigured(response.data.configured || false);
      setSmtpSettings({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',
        smtp_from_email: response.data.smtp_from_email || '',
        smtp_from_name: response.data.smtp_from_name || 'BIZIONIX HMS',
        smtp_use_tls: response.data.smtp_use_tls !== false
      });
    } catch (error) {
      console.error('Failed to load SMTP settings');
    }
  };

  const loadProviders = async () => {
    try {
      const res = await api.get('/communication/providers');
      setProviders(res.data);
    } catch (error) {
      console.error('Failed to load providers');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/communication/settings', {
        sms: smsSettings,
        whatsapp: whatsappSettings,
        use_own_communication: useOwnCommunication
      });
      
      // Also save notification settings
      await api.put('/communication/notification-settings', notificationSettings);
      
      toast.success('Communication settings saved successfully');
      
      // Reload to get updated settings source
      loadSettings();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setTesting(true);
    try {
      const res = await api.post('/communication/test', {
        channel: testChannel,
        phone_number: testPhone.startsWith('+') ? testPhone : `+91${testPhone}`,
        message: testMessage
      });
      
      if (res.data.success) {
        toast.success(`Test ${testChannel.toUpperCase()} sent successfully!`);
      } else {
        toast.error(res.data.message || 'Test failed');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Test failed'));
    } finally {
      setTesting(false);
    }
  };

  const handleSmtpSave = async () => {
    setSaving(true);
    try {
      if (!smtpSettings.smtp_host || !smtpSettings.smtp_username || !smtpSettings.smtp_from_email) {
        toast.error('Please fill in all required SMTP fields');
        setSaving(false);
        return;
      }

      if (smtpConfigured && !smtpSettings.smtp_password) {
        await api.patch('/settings/smtp', {
          smtp_host: smtpSettings.smtp_host,
          smtp_port: smtpSettings.smtp_port,
          smtp_username: smtpSettings.smtp_username,
          smtp_from_email: smtpSettings.smtp_from_email,
          smtp_from_name: smtpSettings.smtp_from_name,
          smtp_use_tls: smtpSettings.smtp_use_tls
        });
      } else {
        if (!smtpSettings.smtp_password) {
          toast.error('Password is required for initial SMTP setup');
          setSaving(false);
          return;
        }
        await api.post('/settings/smtp', smtpSettings);
      }

      toast.success('SMTP configuration saved successfully');
      setSmtpConfigured(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save SMTP configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    setSmtpTesting(true);
    try {
      const response = await api.post('/settings/smtp/test');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send test email'));
    } finally {
      setSmtpTesting(false);
    }
  };

  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSmsProviderFields = () => {
    const provider = providers.sms_providers.find(p => p.id === smsSettings.provider);
    return provider?.fields || ['api_key', 'api_secret', 'sender_id'];
  };

  const getWhatsappProviderFields = () => {
    const provider = providers.whatsapp_providers.find(p => p.id === whatsappSettings.provider);
    return provider?.fields || ['api_key', 'api_secret'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communication-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Communication Settings
          </h1>
          <p className="text-muted-foreground mt-1">Configure SMS and WhatsApp for OTP and notifications</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Settings Hierarchy Banner - Only show for hotel_admin and company_admin, not super_admin */}
      {user?.role !== 'super_admin' && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-blue-900">Settings Hierarchy</h3>
                  <p className="text-sm text-blue-700">
                    {settingsSource === 'hotel' && "Using hotel-level settings"}
                    {settingsSource === 'company' && "Using company-level settings (inherited)"}
                    {settingsSource === 'global' && "Using global/Super Admin settings (inherited)"}
                    {settingsSource === 'default' && "Using default settings (configure to enable notifications)"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={useOwnCommunication}
                    onCheckedChange={(checked) => setUseOwnCommunication(checked)}
                    data-testid="use-own-settings-checkbox"
                  />
                  <span className="text-sm font-medium text-blue-800">
                    {user?.role === 'company_admin' ? 'Use Company Settings' : 'Use Hotel Settings'}
                  </span>
                </label>
                <Badge variant="outline" className="bg-white">
                  {settingsSource === 'hotel' && <><Hotel className="h-3 w-3 mr-1" /> Hotel</>}
                  {settingsSource === 'company' && <><Briefcase className="h-3 w-3 mr-1" /> Company</>}
                  {settingsSource === 'global' && <><Server className="h-3 w-3 mr-1" /> Global</>}
                  {settingsSource === 'default' && <><Info className="h-3 w-3 mr-1" /> Default</>}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="sms" className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Events
          </TabsTrigger>
        </TabsList>

        {/* SMS Settings Tab */}
        <TabsContent value="sms" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-blue-500" />
                    SMS Configuration
                  </CardTitle>
                  <CardDescription>Configure SMS provider for OTP and notifications</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={smsSettings.is_enabled ? "default" : "secondary"}>
                    {smsSettings.is_enabled ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Enabled</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Disabled</>
                    )}
                  </Badge>
                  <Switch
                    checked={smsSettings.is_enabled}
                    onCheckedChange={(checked) => setSmsSettings(prev => ({ ...prev, is_enabled: checked }))}
                    data-testid="sms-enable-switch"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>SMS Provider</Label>
                  <Select
                    value={smsSettings.provider}
                    onValueChange={(v) => setSmsSettings(prev => ({ ...prev, provider: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="sms-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.sms_providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {getSmsProviderFields().includes('api_url') && (
                  <div>
                    <Label>API URL</Label>
                    <Input
                      value={smsSettings.api_url}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, api_url: e.target.value }))}
                      placeholder="https://api.yourprovider.com/sms"
                      className="mt-1"
                      data-testid="sms-api-url"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Full URL endpoint for sending SMS
                    </p>
                  </div>
                )}

                {getSmsProviderFields().includes('api_key') && (
                  <div>
                    <Label>API Key / Account SID</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showSecrets.sms_api_key ? "text" : "password"}
                        value={smsSettings.api_key}
                        onChange={(e) => setSmsSettings(prev => ({ ...prev, api_key: e.target.value }))}
                        placeholder="Enter API Key"
                        className="flex-1"
                        data-testid="sms-api-key"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => toggleSecret('sms_api_key')}
                      >
                        {showSecrets.sms_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {getSmsProviderFields().includes('api_secret') && (
                  <div>
                    <Label>API Secret / Auth Token</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showSecrets.sms_api_secret ? "text" : "password"}
                        value={smsSettings.api_secret}
                        onChange={(e) => setSmsSettings(prev => ({ ...prev, api_secret: e.target.value }))}
                        placeholder="Enter API Secret"
                        className="flex-1"
                        data-testid="sms-api-secret"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => toggleSecret('sms_api_secret')}
                      >
                        {showSecrets.sms_api_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {getSmsProviderFields().includes('sender_id') && (
                  <div>
                    <Label>Sender ID / From Number</Label>
                    <Input
                      value={smsSettings.sender_id}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, sender_id: e.target.value }))}
                      placeholder="e.g., HTLBIL or +1234567890"
                      className="mt-1"
                      data-testid="sms-sender-id"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Alphanumeric sender ID or phone number (depends on provider)
                    </p>
                  </div>
                )}

                {getSmsProviderFields().includes('sender_number') && (
                  <div>
                    <Label>Sender Number</Label>
                    <Input
                      value={smsSettings.sender_number}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, sender_number: e.target.value }))}
                      placeholder="e.g., 9493333747"
                      className="mt-1"
                      data-testid="sms-sender-number"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Sender phone number for URL-based APIs
                    </p>
                  </div>
                )}

                {getSmsProviderFields().includes('template_id') && (
                  <div>
                    <Label>DLT Template ID (India)</Label>
                    <Input
                      value={smsSettings.template_id}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, template_id: e.target.value }))}
                      placeholder="e.g., 1207175299467151781"
                      className="mt-1"
                      data-testid="sms-template-id"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      DLT template ID for transactional SMS in India
                    </p>
                  </div>
                )}

                {getSmsProviderFields().includes('category') && (
                  <div>
                    <Label>SMS Category</Label>
                    <Input
                      value={smsSettings.category}
                      onChange={(e) => setSmsSettings(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g., 2"
                      className="mt-1"
                      data-testid="sms-category"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      SMS category code (if required by provider)
                    </p>
                  </div>
                )}

                {smsSettings.provider === 'custom' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium">Custom URL API Format</p>
                        <p className="mt-1 text-amber-600">
                          For URL-based SMS APIs, you can use placeholders in the API URL:
                        </p>
                        <ul className="list-disc list-inside mt-1 space-y-1 text-amber-600 text-xs">
                          <li><code>{'{phone}'}</code> or <code>{'{mobileno}'}</code> - Recipient phone number</li>
                          <li><code>{'{message}'}</code> - Message text (URL encoded)</li>
                          <li><code>{'{userid}'}</code> - API Key value</li>
                          <li><code>{'{password}'}</code> - API Secret value</li>
                          <li><code>{'{sender}'}</code> or <code>{'{sendername}'}</code> - Sender ID value</li>
                        </ul>
                        <p className="mt-2 text-amber-600 text-xs">
                          Example: <code>https://sms.example.com/SendSMS.aspx?userid={'{userid}'}&password={'{password}'}&mobileno={'{mobileno}'}&message={'{message}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTestChannel('sms');
                    setTestDialogOpen(true);
                  }}
                  disabled={!smsSettings.is_enabled}
                  data-testid="test-sms-btn"
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  Test SMS
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Settings Tab */}
        <TabsContent value="whatsapp" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                    WhatsApp Configuration
                  </CardTitle>
                  <CardDescription>Configure WhatsApp Business API for OTP and notifications</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={whatsappSettings.is_enabled ? "default" : "secondary"}>
                    {whatsappSettings.is_enabled ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Enabled</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Disabled</>
                    )}
                  </Badge>
                  <Switch
                    checked={whatsappSettings.is_enabled}
                    onCheckedChange={(checked) => setWhatsappSettings(prev => ({ ...prev, is_enabled: checked }))}
                    data-testid="whatsapp-enable-switch"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>WhatsApp Provider</Label>
                  <Select
                    value={whatsappSettings.provider}
                    onValueChange={(v) => setWhatsappSettings(prev => ({ ...prev, provider: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="whatsapp-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.whatsapp_providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {getWhatsappProviderFields().includes('api_url') && (
                  <div>
                    <Label>API URL</Label>
                    <Input
                      value={whatsappSettings.api_url}
                      onChange={(e) => setWhatsappSettings(prev => ({ ...prev, api_url: e.target.value }))}
                      placeholder="https://api.yourprovider.com/whatsapp"
                      className="mt-1"
                      data-testid="whatsapp-api-url"
                    />
                  </div>
                )}

                {getWhatsappProviderFields().includes('api_key') && (
                  <div>
                    <Label>
                      {whatsappSettings.provider === 'meta_cloud' ? 'Access Token' : 'API Key / Account SID'}
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showSecrets.wa_api_key ? "text" : "password"}
                        value={whatsappSettings.api_key}
                        onChange={(e) => setWhatsappSettings(prev => ({ ...prev, api_key: e.target.value }))}
                        placeholder="Enter API Key / Access Token"
                        className="flex-1"
                        data-testid="whatsapp-api-key"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => toggleSecret('wa_api_key')}
                      >
                        {showSecrets.wa_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {getWhatsappProviderFields().includes('api_secret') && (
                  <div>
                    <Label>API Secret / Auth Token</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showSecrets.wa_api_secret ? "text" : "password"}
                        value={whatsappSettings.api_secret}
                        onChange={(e) => setWhatsappSettings(prev => ({ ...prev, api_secret: e.target.value }))}
                        placeholder="Enter API Secret"
                        className="flex-1"
                        data-testid="whatsapp-api-secret"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => toggleSecret('wa_api_secret')}
                      >
                        {showSecrets.wa_api_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {getWhatsappProviderFields().includes('sender_id') && whatsappSettings.provider === 'twilio' && (
                  <div>
                    <Label>WhatsApp Number (Twilio)</Label>
                    <Input
                      value={whatsappSettings.sender_id}
                      onChange={(e) => setWhatsappSettings(prev => ({ ...prev, sender_id: e.target.value }))}
                      placeholder="e.g., +14155238886"
                      className="mt-1"
                      data-testid="whatsapp-sender-id"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Your Twilio WhatsApp-enabled number
                    </p>
                  </div>
                )}

                {getWhatsappProviderFields().includes('phone_number_id') && (
                  <div>
                    <Label>Phone Number ID (Meta)</Label>
                    <Input
                      value={whatsappSettings.phone_number_id}
                      onChange={(e) => setWhatsappSettings(prev => ({ ...prev, phone_number_id: e.target.value }))}
                      placeholder="e.g., 123456789012345"
                      className="mt-1"
                      data-testid="whatsapp-phone-number-id"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      From Meta Business Suite → WhatsApp → API Setup
                    </p>
                  </div>
                )}

                {getWhatsappProviderFields().includes('business_account_id') && (
                  <div>
                    <Label>Business Account ID (Meta)</Label>
                    <Input
                      value={whatsappSettings.business_account_id}
                      onChange={(e) => setWhatsappSettings(prev => ({ ...prev, business_account_id: e.target.value }))}
                      placeholder="e.g., 123456789012345"
                      className="mt-1"
                      data-testid="whatsapp-business-account-id"
                    />
                  </div>
                )}
              </div>

              <Separator />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">WhatsApp Business API Setup</p>
                    <ul className="list-disc list-inside mt-1 space-y-1 text-blue-600">
                      <li><strong>Twilio:</strong> Sign up at twilio.com, enable WhatsApp sandbox or register a number</li>
                      <li><strong>Meta Cloud:</strong> Create a Meta Business account, set up WhatsApp Business API</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTestChannel('whatsapp');
                    setTestDialogOpen(true);
                  }}
                  disabled={!whatsappSettings.is_enabled}
                  data-testid="test-whatsapp-btn"
                >
                  <TestTube className="h-4 w-4 mr-2" />
                  Test WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Settings Tab */}
        <TabsContent value="smtp" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-purple-500" />
                      SMTP Server Configuration
                    </CardTitle>
                    <CardDescription>Configure email settings for password reset and notifications</CardDescription>
                  </div>
                  <Badge variant={smtpConfigured ? "default" : "secondary"}>
                    {smtpConfigured ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Configured</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>SMTP Host *</Label>
                    <Input
                      placeholder="smtp.gmail.com"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port *</Label>
                    <Input
                      type="number"
                      placeholder="587"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-10"
                        placeholder="your-email@gmail.com"
                        value={smtpSettings.smtp_username}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                        data-testid="smtp-username-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Password {smtpConfigured ? '(leave blank to keep current)' : '*'}</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          type={showSecrets.smtp_password ? "text" : "password"}
                          className="pl-10"
                          placeholder={smtpConfigured ? '••••••••' : 'App password'}
                          value={smtpSettings.smtp_password}
                          onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                          data-testid="smtp-password-input"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => toggleSecret('smtp_password')}
                      >
                        {showSecrets.smtp_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>From Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        className="pl-10"
                        placeholder="noreply@yourdomain.com"
                        value={smtpSettings.smtp_from_email}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>From Name</Label>
                    <Input
                      placeholder="BIZIONIX HMS"
                      value={smtpSettings.smtp_from_name}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                      data-testid="smtp-from-name-input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={smtpSettings.smtp_use_tls}
                    onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, smtp_use_tls: checked }))}
                    data-testid="smtp-tls-switch"
                  />
                  <Label>Use TLS (recommended)</Label>
                </div>

                <Separator />

                <div className="flex gap-3">
                  <Button onClick={handleSmtpSave} disabled={saving} data-testid="save-smtp-button">
                    {saving ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" /> Save SMTP Settings</>
                    )}
                  </Button>
                  {smtpConfigured && (
                    <Button
                      variant="outline"
                      onClick={handleSmtpTest}
                      disabled={smtpTesting}
                      data-testid="test-smtp-button"
                    >
                      {smtpTesting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" /> Send Test Email</>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SMTP Help Section */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Setup Guide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-medium mb-1">Gmail</h4>
                    <ul className="text-muted-foreground space-y-1">
                      <li>• Host: smtp.gmail.com</li>
                      <li>• Port: 587</li>
                      <li>• Use App Password</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Outlook/Microsoft 365</h4>
                    <ul className="text-muted-foreground space-y-1">
                      <li>• Host: smtp.office365.com</li>
                      <li>• Port: 587</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Custom SMTP</h4>
                    <ul className="text-muted-foreground space-y-1">
                      <li>• Contact your email provider</li>
                      <li>• Common ports: 587 (TLS), 465 (SSL)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Features Enabled</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      {smtpConfigured ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className={smtpConfigured ? '' : 'text-muted-foreground'}>
                        Password Reset Emails
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {smtpConfigured ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className={smtpConfigured ? '' : 'text-muted-foreground'}>
                        Welcome Emails
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {smtpConfigured ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className={smtpConfigured ? '' : 'text-muted-foreground'}>
                        Notification Emails
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Notification Events Tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-purple-500" />
                  Notification Events
                </CardTitle>
                <CardDescription>
                  Configure which events trigger notifications and via which channels
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Booking Notifications */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Booking Confirmation</h4>
                        <p className="text-xs text-muted-foreground">Send when a new booking is created</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.booking_enabled}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, booking_enabled: checked }))}
                      data-testid="booking-notification-switch"
                    />
                  </div>
                  {notificationSettings.booking_enabled && (
                    <div className="space-y-2">
                      <Label className="text-xs">Send via:</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={notificationSettings.booking_channels?.includes('email') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.booking_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              booking_channels: channels.includes('email') 
                                ? channels.filter(c => c !== 'email')
                                : [...channels, 'email']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Mail className="h-3 w-3 mr-1" /> Email
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.booking_channels?.includes('sms') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.booking_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              booking_channels: channels.includes('sms') 
                                ? channels.filter(c => c !== 'sms')
                                : [...channels, 'sms']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Phone className="h-3 w-3 mr-1" /> SMS
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.booking_channels?.includes('whatsapp') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.booking_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              booking_channels: channels.includes('whatsapp') 
                                ? channels.filter(c => c !== 'whatsapp')
                                : [...channels, 'whatsapp']
                            }));
                          }}
                          className="text-xs"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Check-in Notifications */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <LogIn className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Check-in Welcome</h4>
                        <p className="text-xs text-muted-foreground">Send when guest checks in</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.checkin_enabled}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, checkin_enabled: checked }))}
                      data-testid="checkin-notification-switch"
                    />
                  </div>
                  {notificationSettings.checkin_enabled && (
                    <div className="space-y-2">
                      <Label className="text-xs">Send via:</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={notificationSettings.checkin_channels?.includes('email') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkin_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkin_channels: channels.includes('email') 
                                ? channels.filter(c => c !== 'email')
                                : [...channels, 'email']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Mail className="h-3 w-3 mr-1" /> Email
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.checkin_channels?.includes('sms') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkin_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkin_channels: channels.includes('sms') 
                                ? channels.filter(c => c !== 'sms')
                                : [...channels, 'sms']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Phone className="h-3 w-3 mr-1" /> SMS
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.checkin_channels?.includes('whatsapp') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkin_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkin_channels: channels.includes('whatsapp') 
                                ? channels.filter(c => c !== 'whatsapp')
                                : [...channels, 'whatsapp']
                            }));
                          }}
                          className="text-xs"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Check-out Notifications */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <LogOut className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Check-out Thank You</h4>
                        <p className="text-xs text-muted-foreground">Send when guest checks out</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.checkout_enabled}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({ ...prev, checkout_enabled: checked }))}
                      data-testid="checkout-notification-switch"
                    />
                  </div>
                  {notificationSettings.checkout_enabled && (
                    <div className="space-y-2">
                      <Label className="text-xs">Send via:</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={notificationSettings.checkout_channels?.includes('email') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkout_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkout_channels: channels.includes('email') 
                                ? channels.filter(c => c !== 'email')
                                : [...channels, 'email']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Mail className="h-3 w-3 mr-1" /> Email
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.checkout_channels?.includes('sms') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkout_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkout_channels: channels.includes('sms') 
                                ? channels.filter(c => c !== 'sms')
                                : [...channels, 'sms']
                            }));
                          }}
                          className="text-xs"
                        >
                          <Phone className="h-3 w-3 mr-1" /> SMS
                        </Button>
                        <Button
                          size="sm"
                          variant={notificationSettings.checkout_channels?.includes('whatsapp') ? 'default' : 'outline'}
                          onClick={() => {
                            const channels = notificationSettings.checkout_channels || [];
                            setNotificationSettings(prev => ({
                              ...prev,
                              checkout_channels: channels.includes('whatsapp') 
                                ? channels.filter(c => c !== 'whatsapp')
                                : [...channels, 'whatsapp']
                            }));
                          }}
                          className="text-xs"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Configuration Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Channel Status</CardTitle>
                <CardDescription>Configuration status for each notification channel</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-blue-500" />
                    <span className="font-medium">Email (SMTP)</span>
                  </div>
                  <Badge variant={smtpConfigured ? "default" : "secondary"}>
                    {smtpConfigured ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Configured</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                    )}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-orange-500" />
                    <span className="font-medium">SMS</span>
                  </div>
                  <Badge variant={smsSettings.is_enabled ? "default" : "secondary"}>
                    {smsSettings.is_enabled ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Configured</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                    )}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                    <span className="font-medium">WhatsApp</span>
                  </div>
                  <Badge variant={whatsappSettings.is_enabled ? "default" : "secondary"}>
                    {whatsappSettings.is_enabled ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Configured</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                    )}
                  </Badge>
                </div>
                
                <Separator className="my-4" />
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 mb-1">Important</p>
                      <p className="text-amber-700">
                        Notifications will only be sent via channels that are properly configured. 
                        Make sure to set up your preferred channels in the SMS, WhatsApp, or Email tabs.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-md" data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5" />
              Test {testChannel === 'sms' ? 'SMS' : 'WhatsApp'}
            </DialogTitle>
            <DialogDescription>
              Send a test message to verify your configuration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Phone Number</Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+91 9876543210"
                className="mt-1"
                data-testid="test-phone-input"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Test message"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTestDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleTest} disabled={testing} className="flex-1">
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
